import { Box, Flex, Image, Table, Tbody, Td, Text, Tr, useDisclosure } from "@chakra-ui/react";
import { IoIosArrowRoundDown } from "react-icons/io";
import { FormattedMessage, useIntl } from "react-intl";

import {
  RanaButton,
  RanaContextMenu,
  RanaLoading,
  RanaModal,
  TrashIcon,
  UndoIcon,
} from "@nens/ranalib";
import { humanizeBytes } from "@nens/shared-utils";

import { DetailRow, DetailTable } from "@/components/detail-table";
import { DownloadIconLink } from "@/components/download-icon-link";
import { HeaderActionSection } from "@/components/header-action-section";
import { useIconSrc } from "@/hooks/use-icon-src";
import { useHccGridadminQuery } from "@/queries/query-hcc-grid-admin";
import { useThreediVersion } from "@/queries/query-hcc-version";
import { modelIsBeingGenerated } from "@/utils/model-is-being-generated";
import { scrollToElementAndFlash } from "@/utils/scroll-to-element-and-flash";

import {
  useCreateThreediModelMutation,
  useDeleteThreediModelMutation,
  useRecreateThreediModelMutation,
} from "../api/hcc-schematisation-revision-model";
import { MODEL_TASK_TABLE_ID } from "../consts/model-task-table-id";
import { useModelSchematisationPermission } from "../hooks/use-model-schematisation-permission";
import { useSchematisationDetailsContext } from "../schematisation-details-context";

export function SchematisationModelSection() {
  const { schematisation, revision, threediModel } = useSchematisationDetailsContext();
  const schematisationId = schematisation.id;
  const revisionId = revision.id;

  const intl = useIntl();
  const { canEditModelSchema } = useModelSchematisationPermission(schematisationId);
  const { data: gridadmin, isLoading: isGridadminLoading } = useHccGridadminQuery(threediModel?.id);
  const { threediVersion, isLoading: isThreediVersionLoading } = useThreediVersion();
  const deleteModelMutation = useDeleteThreediModelMutation(schematisationId, revisionId);
  const recreateModelMutation = useRecreateThreediModelMutation(schematisationId, revisionId);
  const createModelMutation = useCreateThreediModelMutation(schematisationId, revisionId);
  const deleteDisclosure = useDisclosure();
  const updateDisclosure = useDisclosure();
  const noModelYetImageSrc = useIconSrc("folder");

  const isDataLoading = isGridadminLoading || isThreediVersionLoading;

  const rows: DetailRow[] = [
    {
      name: intl.formatMessage({ id: "file.revision.model_id", defaultMessage: "Rana model id" }),
      value: threediModel?.id,
    },
    {
      name: intl.formatMessage({ id: "file.projection", defaultMessage: "Projection" }),
      value: threediModel?.epsg ? `EPSG:${threediModel.epsg}` : "-",
    },
    {
      name: intl.formatMessage({ id: "file.node_count", defaultMessage: "Node count" }),
      value: threediModel?.nodesCount,
    },
    {
      name: intl.formatMessage({ id: "file.lines_count", defaultMessage: "Lines count" }),
      value: threediModel?.linesCount,
    },
    {
      name: intl.formatMessage({ id: "file.breach_count", defaultMessage: "Breach count" }),
      value: threediModel?.breachCount,
    },
  ];

  const gridadminName = intl.formatMessage({
    id: "file.downloads.gridadmin",
    defaultMessage: "Gridadmin file",
  });

  if (gridadmin?.getUrl) {
    rows.push({
      name: gridadminName,
      value: `gridadmin.h5 (${humanizeBytes(gridadmin.size ?? 0)})`,
      action: <DownloadIconLink filename="gridadmin.h5" fetchUrl={async () => gridadmin.getUrl} />,
    });
  } else if (!isGridadminLoading && threediModel && !gridadmin) {
    rows.push({
      name: gridadminName,
      value: intl.formatMessage({
        id: "file.downloads.gridadmin_unavailable",
        defaultMessage: "No gridadmin file available",
      }),
    });
  }

  const deleteModelText = intl.formatMessage({
    id: "file.model.delete",
    defaultMessage: "Delete this model",
  });
  const updateModelText = intl.formatMessage(
    {
      id: "file.model.update_to_version",
      defaultMessage: "Update model to version {version}",
    },
    { version: threediVersion ?? "" }
  );
  const threediVersionUnknown = !isThreediVersionLoading && threediVersion === undefined;
  const showMenu = !isDataLoading && canEditModelSchema && !!threediModel;
  const revisionIsInvalid = revision.isValid === false;
  const revisionIsBeingChecked = revision.isValid === null;
  const isModelBeingGenerated = modelIsBeingGenerated(threediModel);

  const getEmptyStateContent = (): JSX.Element => {
    if (isModelBeingGenerated) {
      return (
        <>
          <Text color="brand.analytic">
            <FormattedMessage
              id="file.model.being_generated"
              defaultMessage="A Rana model of this revision is being generated"
            />
          </Text>
          <RanaButton
            onClick={() => {
              const element = document.getElementById(MODEL_TASK_TABLE_ID);
              if (element) {
                scrollToElementAndFlash(element);
              }
            }}
            variant="fourthiary"
            rightIcon={<IoIosArrowRoundDown />}
          >
            <FormattedMessage
              id="file.model.see_progress_in_model_tasks"
              defaultMessage="See progress in model tasks"
            />
          </RanaButton>
        </>
      );
    }

    if (revisionIsInvalid) {
      return (
        <Text color="brand.analytic">
          <FormattedMessage
            id="file.model.no_model"
            defaultMessage="This revision has no model. As it is invalid, no model can be created"
          />
        </Text>
      );
    }

    if (revisionIsBeingChecked) {
      return (
        <Text color="brand.analytic">
          <FormattedMessage
            id="file.model.no_model_yet"
            defaultMessage="This revision's validity is being checked."
          />
        </Text>
      );
    }

    return (
      <>
        <Text color="brand.analytic">
          <FormattedMessage
            id="file.model.no_model_yet"
            defaultMessage="This revision does not have a model yet"
          />
        </Text>
        {canEditModelSchema && (
          <RanaButton
            variant="primary"
            leftIcon={<UndoIcon boxSize={4} />}
            isLoading={createModelMutation.isMutating}
            onClick={() => {
              createModelMutation.mutate();
            }}
          >
            <FormattedMessage id="file.model.generate" defaultMessage="(Re)Generate model" />
          </RanaButton>
        )}
      </>
    );
  };

  const renderModelSectionContent = () => {
    if (isDataLoading || (threediModel && !isModelBeingGenerated)) {
      return <DetailTable rows={rows} isLoading={isDataLoading} />;
    }

    return (
      <Box w="full" overflow="auto">
        <Table variant="grid">
          <Tbody>
            <Tr>
              <Td borderTopStyle="none">
                <Flex
                  my={8}
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  gap={4}
                >
                  {isModelBeingGenerated || revisionIsBeingChecked ? (
                    <RanaLoading />
                  ) : (
                    noModelYetImageSrc && <Image height="24" width="24" src={noModelYetImageSrc} />
                  )}
                  {getEmptyStateContent()}
                </Flex>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    );
  };

  const menuButton = showMenu ? (
    <RanaContextMenu
      actions={[
        {
          label: deleteModelText,
          icon: <TrashIcon boxSize={4} />,
          action: deleteDisclosure.onOpen,
        },
        {
          label: updateModelText,
          icon: <UndoIcon boxSize={4} />,
          action: updateDisclosure.onOpen,
          ...(threediVersionUnknown
            ? {
                disabledText: intl.formatMessage({
                  id: "file.model.version_unknown",
                  defaultMessage: "The current HCC framework version could not be determined",
                }),
              }
            : {}),
        },
      ]}
    />
  ) : undefined;

  return (
    <>
      <HeaderActionSection
        title={intl.formatMessage({ id: "section.model", defaultMessage: "Model" })}
        action={{ type: "menu", menuButton }}
      >
        {renderModelSectionContent}
      </HeaderActionSection>

      <RanaModal
        isOpen={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        topBar={deleteModelText}
        buttons={[
          {
            children: <FormattedMessage id="button.delete" defaultMessage="Delete" />,
            variant: "danger",
            isDisabled: deleteModelMutation.isMutating,
            onClick: () => {
              if (!threediModel?.id) return;
              deleteModelMutation.mutate(threediModel.id, {
                onSuccess: deleteDisclosure.onClose,
              });
            },
          },
        ]}
      >
        <FormattedMessage
          id="file.model.delete_are_you_sure"
          defaultMessage="Are you sure you want to delete this model? This action cannot be undone."
        />
      </RanaModal>

      <RanaModal
        isOpen={updateDisclosure.isOpen}
        onClose={updateDisclosure.onClose}
        topBar={updateModelText}
        buttons={[
          {
            children: <FormattedMessage id="button.update" defaultMessage="Update" />,
            variant: "danger",
            isDisabled: recreateModelMutation.isMutating,
            onClick: () => {
              if (!threediModel?.id) return;
              recreateModelMutation.mutate(threediModel.id, {
                onSuccess: updateDisclosure.onClose,
              });
            },
          },
        ]}
      >
        <FormattedMessage
          id="file.model.update_are_you_sure"
          defaultMessage="This deletes the current model and generates a new one from this revision. This action cannot be undone."
        />
      </RanaModal>
    </>
  );
}
